// Web shim for react-native-sound
// Provides audio playback using HTMLAudioElement on web

class SoundWeb {
  private audioContext: AudioContext | null = null;
  private audioElement: HTMLAudioElement | null = null;
  private uri: string;

  constructor(uri: string) {
    this.uri = uri;
    try {
      this.audioElement = new Audio(uri);
      this.audioElement.crossOrigin = 'anonymous';
    } catch (err) {
      console.warn('Failed to initialize SoundWeb:', err);
    }
  }

  play(onComplete?: (success: boolean) => void): void {
    if (!this.audioElement) {
      if (onComplete) onComplete(false);
      return;
    }

    this.audioElement
      .play()
      .then(() => {
        if (onComplete) {
          // Wait for audio to finish
          this.audioElement?.addEventListener('ended', () => {
            onComplete(true);
          }, { once: true });
        }
      })
      .catch((err) => {
        console.warn('Audio play failed:', err);
        if (onComplete) onComplete(false);
      });
  }

  pause(): void {
    if (this.audioElement) {
      this.audioElement.pause();
    }
  }

  stop(): void {
    if (this.audioElement) {
      this.audioElement.pause();
      this.audioElement.currentTime = 0;
    }
  }

  release(): void {
    if (this.audioElement) {
      this.audioElement.pause();
      this.audioElement = null;
    }
  }

  setVolume(volume: number): void {
    if (this.audioElement) {
      this.audioElement.volume = Math.max(0, Math.min(1, volume));
    }
  }

  getCurrentTime(onComplete?: (position: number) => void): void {
    if (this.audioElement && onComplete) {
      onComplete(this.audioElement.currentTime);
    }
  }

  setCurrentTime(seconds: number): void {
    if (this.audioElement) {
      this.audioElement.currentTime = seconds;
    }
  }

  getDuration(onComplete?: (duration: number) => void): void {
    if (this.audioElement && onComplete) {
      if (this.audioElement.duration) {
        onComplete(this.audioElement.duration);
      } else {
        // Wait for metadata to load
        this.audioElement.addEventListener('loadedmetadata', () => {
          onComplete(this.audioElement?.duration || 0);
        }, { once: true });
      }
    }
  }
}

// Export Sound class
export default class Sound {
  static setCategory(category: string, mixWithOthers?: boolean): void {
    // No-op on web
  }

  static setActive(active: boolean): void {
    // No-op on web
  }

  constructor(uri: string) {
    return new SoundWeb(uri) as any;
  }
}
